import { ChevronDownIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { FilterOption } from '@odo/shared-ui';
import AssigneeSelector from '../../../conversations/AssigneeSelector';
import { useUser } from '../../../../providers/UserProvider';
import type {
    Communication,
    CommunicationPatientRef,
    CommunicationStatus
} from '../../../../services/communications';
import type { PracticeDetail } from '../../../../services/practiceDetail';
import { CategorySelector } from './CategorySelector';
import { LinkedPatientsCard } from './LinkedPatientsCard';
import { MetaCard } from './MetaCard';
import { PracticeMetaCard } from './PracticeMetaCard';

const STATUS_DOT: Record<CommunicationStatus, string> = {
    open: '#2C6F67',
    resolved: '#059669'
};

interface CommunicationMetaRailProps {
    communication: Communication;
    practice: PracticeDetail | null;
    practiceOptions: readonly FilterOption[];
    isPracticeOptionsLoading: boolean;
    totalCommunications: number;
    openCommunicationsCount: number;
    isSaving: boolean;
    onPracticeChange: (practiceId: string) => void;
    onPatientsChange: (patients: CommunicationPatientRef[]) => void;
    onCategoriesChange: (categories: string[]) => void;
    onStatusChange: (status: CommunicationStatus) => void;
    onAssign: (email: string) => void;
    onUnassign: () => void;
    onDeleteClick: () => void;
}

// Thread detail page's meta rail. Every edit here PATCHes immediately (no
// draft/save step, unlike the create page). Five cards: Practice, Status,
// Categories, Linked patients, Assignee, then a Delete-thread action.
export function CommunicationMetaRail({
    communication,
    practice,
    practiceOptions,
    isPracticeOptionsLoading,
    totalCommunications,
    openCommunicationsCount,
    isSaving,
    onPracticeChange,
    onPatientsChange,
    onCategoriesChange,
    onStatusChange,
    onAssign,
    onUnassign,
    onDeleteClick
}: CommunicationMetaRailProps) {
    const { user } = useUser();

    return (
        <div className="w-1/5 min-w-[280px] shrink-0 h-full overflow-y-auto scrollbar-hide pr-1 flex flex-col gap-4">
            <PracticeMetaCard
                editable={!communication.practiceId}
                practiceId={communication.practiceId}
                practice={practice}
                totalCommunications={totalCommunications}
                openCommunicationsCount={openCommunicationsCount}
                practiceOptions={practiceOptions}
                isPracticeOptionsLoading={isPracticeOptionsLoading}
                onPracticeChange={onPracticeChange}
            />

            <MetaCard title="Status">
                <div className="relative">
                    <span
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full pointer-events-none"
                        style={{ background: STATUS_DOT[communication.status] }}
                    />
                    <select
                        value={communication.status}
                        disabled={isSaving}
                        onChange={(e) => onStatusChange(e.target.value as CommunicationStatus)}
                        className="w-full h-9 pl-6 pr-8 border border-gray-300 rounded-lg text-[13px] text-gray-800 bg-white appearance-none focus:outline-none focus:border-gray-800 focus:ring-2 focus:ring-gray-200 disabled:opacity-50"
                    >
                        <option value="open">Open</option>
                        <option value="resolved">Resolved</option>
                    </select>
                    <ChevronDownIcon className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
            </MetaCard>

            <MetaCard title="Categories">
                <CategorySelector
                    categories={communication.categories}
                    disabled={isSaving}
                    onChange={onCategoriesChange}
                />
            </MetaCard>

            <LinkedPatientsCard
                practiceId={communication.practiceId}
                patients={communication.patients}
                onChange={onPatientsChange}
            />

            <MetaCard title="Assignee">
                <AssigneeSelector
                    assignedTo={communication.assignee?.email ?? null}
                    onAssign={onAssign}
                    onUnassign={onUnassign}
                    currentUserEmail={user?.email ?? ''}
                    dropdownAlign="right"
                />
            </MetaCard>

            <button
                type="button"
                onClick={onDeleteClick}
                className="w-full h-9 shrink-0 rounded-lg border border-red-200 bg-white text-[13px] font-semibold text-red-600 hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
            >
                <TrashIcon className="h-3.5 w-3.5" />
                Delete communication
            </button>
        </div>
    );
}
